import { TextButton, TextInput, utils } from "@tfo/common"
import { Dispatch, FormEvent, SetStateAction, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import $http, { HttpError } from '../../utils/$http'
import './VerificationCodeLogin.scss'


type VerificationForm = {
    email: string,
    emailTouched: boolean,
    emailErrorMessage?: string,
    verificationCode: string,
    verificationCodeTouched: boolean,
    verificationCodeErrorMessage?: string
}

const VerificationCodeLogin = ({ onProgress }: {
    onProgress: (isRunning: boolean) => void,
}
) => {
    const intl = useIntl()

    const [isInVerificationStage, setVerificationStage] = useState(false)
    const [loginFail, setLoginFail] = useState({
        badFormat: false,
        invalidCode: false,
        generic: false
    })

    const [loginForm, setLoginForm] = useState<VerificationForm>({
        email: "",
        emailTouched: false,
        emailErrorMessage: null,
        verificationCode: "",
        verificationCodeTouched: false,
        verificationCodeErrorMessage: null
    })

    const onSendVerificationCode = async () => {
        onProgress(true)

        try {
            await $http.post('authentication/verification-code/create', {
                email: loginForm.email,
            })
        }
        finally {
            setVerificationStage(true)
            onProgress(false)
        }
    }

    const onVerifyVerificationCode = async () => {
        onProgress(true)
        setLoginFail({ badFormat: false, invalidCode: false, generic: false })

        try {
            const response = await $http.post<any>('authentication/verification-code/verify', {
                email: loginForm.email,
                verificationCode: loginForm.verificationCode
            })
            utils.setCookie(JSON.stringify(response))
            window.location.href = '/candidate'
        } catch (error) {
            const httpError = error as HttpError
            switch (httpError.code) {
                case '400':
                    setLoginFail(prev => { return { ...prev, badFormat: true } })
                    break
                case '401':
                    setLoginFail(prev => { return { ...prev, invalidCode: true } })
                    break
                default:
                    setLoginFail(prev => { return { ...prev, generic: true } })
            }
        }
        finally {
            onProgress(false)
        }
    }

    const onFormSubmit = (e: FormEvent) => {
        e.preventDefault()
        if (isInVerificationStage) {
            onVerifyVerificationCode()
        }
        else {
            onSendVerificationCode()
        }

        return false
    }


    return (
        <form className="LoginIndividualVerificationCode" onSubmit={onFormSubmit}>
            <VerificationCodeFormInput
                prop="email"
                loginForm={loginForm}
                setLoginForm={setLoginForm}
                formatErrorMessage={intl.formatMessage({ id: 'login.verificationcode.email.format' })}
                type="email"
                required
                className="Email"
                placeholder={intl.formatMessage({ id: 'common.email' })}
                title={intl.formatMessage({ id: 'login.verificationcode.email.format' })}
            />

            {!isInVerificationStage &&
                <TextButton id="sendCode">
                    <FormattedMessage id="login.verificationcode.send" />
                </TextButton>
            }

            {isInVerificationStage &&
                <div>
                    <VerificationCodeFormInput
                        prop="verificationCode"
                        loginForm={loginForm}
                        setLoginForm={setLoginForm}
                        formatErrorMessage={intl.formatMessage({ id: 'login.verificationcode.code.format' })}
                        inputMode="numeric"
                        required
                        className="VerificationCode"
                        placeholder={intl.formatMessage({ id: 'common.verificationCode' })}
                        title={intl.formatMessage({ id: 'login.verificationcode.code.format' })}
                    />
                    <FormattedMessage id="login.verificationcode.sent" />
                    <TextButton id="login">
                        <FormattedMessage id="common.login" />
                    </TextButton>
                    {(loginFail.badFormat || loginFail.invalidCode || loginFail.generic) &&
                        <FormattedMessage id="login.verificationcode.error.code" />}
                </div>
            }


        </form>
    )
}

const VerificationCodeFormInput = ({ loginForm, setLoginForm, prop, formatErrorMessage, ...rest }: {
    loginForm: VerificationForm,
    setLoginForm: Dispatch<SetStateAction<VerificationForm>>,
    prop: string
    formatErrorMessage: string,
} & React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>) => {

    const errorProp = `${prop}ErrorMessage`
    const touchedProp = `${prop}Touched`

    return (<TextInput
        value={loginForm[prop]}
        errorMessage={loginForm[errorProp]}
        hasError={!!loginForm[errorProp]}
        onChange={e => setLoginForm(prev => ({
            ...prev,
            [prop]: e.target.value,
            [errorProp]: null
        }))}
        onBlur={() => setLoginForm(prev => ({
            ...prev,
            [touchedProp]: true
        }))}
        onInvalid={() => setLoginForm(prev => ({
            ...prev,
            [errorProp]: formatErrorMessage
        }))}
        data-touched={loginForm[touchedProp]}
        {...(rest as any)}
    />)
}

export default VerificationCodeLogin